import React, { useState, useRef } from "react";
import { getAccountHeads, createAccountHead } from "../../core/api/accountHeadService";
import { getSuppliers, createSupplier } from "../../core/api/supplierService";
import { getDoctors, createDoctor } from "../../core/api/doctorService";
import { getProcedures, createProcedure } from "../../core/api/procedureService";
import { getProducts, createProduct } from "../../core/api/productService";
import { getPatients, createPatient } from "../../core/api/patientService";
import { getOpeningStocks, createOpeningStock, getPurchases, createPurchase } from "../../core/api/purchaseService";
import { getAppointments, createAppointment } from "../../core/api/appointmentService";
import { getInvoices, createInvoice } from "../../core/api/invoiceService";
import { getExpenses, createExpense, getIncomeEntries, createIncomeEntry, getJournalEntries, createJournalEntry, getSupplierAdvances, createSupplierAdvance } from "../../core/api/financeService";

// ── date helpers ───────────────────────────────────────────────────────────────
const ago = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
};

// ── seed definitions ───────────────────────────────────────────────────────────

// accountType is only used locally to decide expense/income heads — NOT sent to API
const ACCOUNT_HEADS = [
  { accountName: "Dental Supplies", _type: "expense" },
  { accountName: "Staff Salaries",  _type: "expense" },
  { accountName: "Rent",            _type: "expense" },
  { accountName: "Utilities",       _type: "expense" },
  { accountName: "Consultation Income", _type: "income" },
  { accountName: "Other Income",    _type: "income" },
  { accountName: "Cash",            _type: "asset" },
  { accountName: "Bank",            _type: "asset" },
];

// Correct supplier fields — matches SupplierModal
const SUPPLIERS = [
  { supplierName: "Dental Supplies Ltd",    contactPersonName: "Ravi Kumar",  mobileNumber: "9876543210", email: "ravi@dentalsupplies.com",  address: "Chennai, Tamil Nadu",    status: "Active" },
  { supplierName: "PharmaMed Distributors", contactPersonName: "Leena Shah",  mobileNumber: "9845612370", email: "leena@pharmamed.com",       address: "Mumbai, Maharashtra",   status: "Active" },
  { supplierName: "OrthoSupply Co",          contactPersonName: "Anil Verma", mobileNumber: "9988776655", email: "anil@orthosupply.com",      address: "Delhi, NCR",             status: "Active" },
  { supplierName: "MedEquip Solutions",      contactPersonName: "Sima Patel", mobileNumber: "9123456789", email: "sima@medequip.com",         address: "Ahmedabad, Gujarat",     status: "Active" },
];

const DOCTORS = [
  { doctorName: "Dr. Rahul Sharma",  specialization: "Orthodontics",     qualification: "BDS, MDS", phone: "9876501234", email: "rahul@clinic.com",  consultationFee: 700,  status: "Active" },
  { doctorName: "Dr. Priya Menon",   specialization: "General Dentistry", qualification: "BDS",     phone: "9876502345", email: "priya@clinic.com",  consultationFee: 500,  status: "Active" },
  { doctorName: "Dr. Arun Kumar",    specialization: "Oral Surgery",      qualification: "BDS, MDS", phone: "9876503456", email: "arun@clinic.com",   consultationFee: 800,  status: "Active" },
];

const PROCEDURES = [
  { procedureName: "Dental Consultation",     description: "Initial examination",        defaultRate: 500,  duration: 30, status: "Active" },
  { procedureName: "Tooth Extraction",         description: "Removal of damaged tooth",  defaultRate: 1500, duration: 45, status: "Active" },
  { procedureName: "Root Canal Treatment",     description: "RCT for infected tooth",    defaultRate: 5000, duration: 90, status: "Active" },
  { procedureName: "Dental Filling",           description: "Composite filling",         defaultRate: 800,  duration: 40, status: "Active" },
  { procedureName: "Teeth Whitening",          description: "Professional whitening",    defaultRate: 3000, duration: 60, status: "Active" },
  { procedureName: "Scaling and Polishing",    description: "Plaque removal and polish", defaultRate: 1200, duration: 45, status: "Active" },
  { procedureName: "Dental Crown",             description: "Ceramic crown",             defaultRate: 8000, duration: 60, status: "Active" },
  { procedureName: "Orthodontic Consultation", description: "Braces consultation",       defaultRate: 700,  duration: 30, status: "Active" },
];

// itemCategory: only "Medicine" or "Consumable" are valid
const PRODUCTS = [
  { itemName: "Dental Gloves",         itemCategory: "Consumable", unit: "Box",    purchaseRate: 200,  sellingRate: 350,  minimumStockLevel: 5 },
  { itemName: "Anesthesia Cartridge",  itemCategory: "Medicine",   unit: "Piece",  purchaseRate: 50,   sellingRate: 100,  minimumStockLevel: 20 },
  { itemName: "Composite Resin",       itemCategory: "Medicine",   unit: "Nos",    purchaseRate: 500,  sellingRate: 900,  minimumStockLevel: 3 },
  { itemName: "Orthodontic Brackets",  itemCategory: "Consumable", unit: "Kit",    purchaseRate: 1200, sellingRate: 2000, minimumStockLevel: 2 },
  { itemName: "Dental Floss Roll",     itemCategory: "Consumable", unit: "Roll",   purchaseRate: 80,   sellingRate: 150,  minimumStockLevel: 10 },
];

// Correct patient fields — matches PatientModal
const PATIENTS = [
  { patientName: "Ananya Krishnan",  mobileNumber: "9500112233", gender: "Female", dob: "1990-03-15", category: "Normal", address: "12 Rose Street, Chennai" },
  { patientName: "Karthik Rajan",    mobileNumber: "9500223344", gender: "Male",   dob: "1985-07-22", category: "Normal", address: "45 Nehru Nagar, Coimbatore" },
  { patientName: "Deepa Suresh",     mobileNumber: "9500334455", gender: "Female", dob: "1992-11-08", category: "Normal", address: "78 Gandhi Road, Madurai" },
  { patientName: "Vikram Nair",      mobileNumber: "9500445566", gender: "Male",   dob: "1978-05-30", category: "Arab",   address: "23 MG Road, Trichy" },
  { patientName: "Sowmya Balaji",    mobileNumber: "9500556677", gender: "Female", dob: "1995-09-14", category: "Normal", address: "56 Lake View, Salem" },
  { patientName: "Arjun Pillai",     mobileNumber: "9500667788", gender: "Male",   dob: "1988-01-25", category: "Normal", address: "34 Anna Nagar, Chennai" },
  { patientName: "Meera Chandran",   mobileNumber: "9500778899", gender: "Female", dob: "2000-06-18", category: "Normal", address: "67 Pattabi Street, Tirunelveli" },
  { patientName: "Suresh Kumar",     mobileNumber: "9500889900", gender: "Male",   dob: "1975-12-03", category: "Normal", address: "89 Brigade Road, Vellore" },
  { patientName: "Nithya Devi",      mobileNumber: "9500990011", gender: "Female", dob: "1998-04-27", category: "Normal", address: "11 West Street, Erode" },
  { patientName: "Balaji Murugan",   mobileNumber: "9501001122", gender: "Male",   dob: "1982-08-09", category: "Normal", address: "22 South Street, Thanjavur" },
];

// [daysAgo, fromTime, patientIdx, doctorIdx, procedureIdx, status]
const APPT_PLAN = [
  [0,  "09:00", 0, 0, 0, "Booked"],
  [0,  "10:00", 1, 1, 5, "Completed"],
  [0,  "11:00", 2, 2, 1, "Booked"],
  [0,  "14:00", 3, 0, 7, "Completed"],
  [0,  "15:30", 4, 1, 3, "In Progress"],
  [1,  "09:30", 5, 2, 2, "Completed"],
  [1,  "11:00", 6, 0, 4, "Completed"],
  [2,  "10:00", 7, 1, 0, "Completed"],
  [2,  "14:00", 8, 2, 5, "NoShow"],
  [3,  "09:00", 9, 0, 6, "Completed"],
  [3,  "11:30", 0, 1, 1, "Completed"],
  [4,  "10:00", 1, 2, 3, "Completed"],
  [4,  "15:00", 2, 0, 0, "Cancelled"],
  [5,  "09:00", 3, 1, 2, "Completed"],
  [6,  "10:30", 4, 2, 5, "Completed"],
  [8,  "09:00", 5, 0, 4, "Completed"],
  [10, "11:00", 6, 1, 6, "Completed"],
  [12, "10:00", 7, 2, 1, "Completed"],
  [15, "09:30", 8, 0, 3, "Completed"],
  [18, "14:00", 9, 1, 5, "Completed"],
  [20, "10:00", 0, 2, 2, "Completed"],
  [22, "11:00", 1, 0, 0, "Completed"],
  [25, "09:00", 2, 1, 6, "Completed"],
  [28, "14:30", 3, 2, 4, "Completed"],
  [30, "10:00", 4, 0, 1, "Completed"],
];

// [apptIdx, discount, paidFull]
const INVOICE_PLAN = [
  [1, 0, true], [3, 0, true], [5, 100, true], [6, 0, false], [7, 0, true],
  [9, 200, true], [10, 0, true], [11, 0, false], [13, 0, true], [14, 0, true],
  [15, 500, true], [16, 0, true], [17, 0, true], [18, 0, false], [19, 0, true],
  [20, 0, true], [21, 0, true], [22, 100, true], [23, 0, true], [24, 0, true],
];

// [daysAgo, ahDefIdx (0-3=expense), amount, mode, desc]
const EXPENSE_PLAN = [
  [0,  0, 2500,  "Cash", "Gloves and syringes restock"],
  [1,  1, 45000, "Bank", "Monthly staff salary partial"],
  [2,  0, 1800,  "Cash", "Composite resin and bonding agent"],
  [3,  2, 25000, "Bank", "Monthly clinic rent"],
  [5,  3, 3200,  "Cash", "Electricity bill"],
  [7,  0, 4500,  "Cash", "Dental instruments kit"],
  [10, 1, 20000, "Bank", "Dentist assistant salary"],
  [12, 3, 1500,  "Cash", "Water and internet bill"],
  [15, 0, 6000,  "Cash", "X-ray consumables"],
  [18, 2, 25000, "Bank", "Rent advance"],
  [20, 0, 3000,  "Cash", "Disposable items stock"],
  [22, 1, 15000, "Bank", "Receptionist salary"],
  [25, 3, 2800,  "Cash", "Utilities 2 months"],
  [28, 0, 5500,  "Cash", "Dental chair service"],
  [30, 2, 25000, "Bank", "Previous month rent"],
];

// [daysAgo, ahDefIdx (4-5=income), amount, mode, desc]
const INCOME_PLAN = [
  [2,  4, 1500, "Cash", "Extra consultation session"],
  [5,  5, 3000, "Cash", "Dental kit sales"],
  [10, 4, 2000, "Cash", "Late fee collection"],
  [15, 5, 5000, "Bank", "Insurance reimbursement"],
  [20, 4, 1000, "Cash", "Patient registration fee"],
  [25, 5, 4000, "Bank", "Dental awareness camp income"],
  [30, 4, 800,  "Cash", "Sundry income"],
];

// [daysAgo, supplierIdx, [{pi, qty, rate}]]
const PURCHASE_PLAN = [
  [3,  0, [{ pi: 0, qty: 10, rate: 200 }, { pi: 1, qty: 50, rate: 50 }]],
  [7,  1, [{ pi: 2, qty: 5,  rate: 500 }]],
  [12, 2, [{ pi: 3, qty: 8,  rate: 1200 }]],
  [18, 3, [{ pi: 4, qty: 20, rate: 80 }]],
  [24, 0, [{ pi: 0, qty: 15, rate: 195 }, { pi: 1, qty: 100, rate: 48 }]],
  [30, 1, [{ pi: 2, qty: 3,  rate: 510 }]],
];

// [daysAgo, supplierIdx, amount, mode]
const ADVANCE_PLAN = [
  [5,  0, 10000, "Cash"],
  [10, 1, 15000, "Bank"],
  [20, 2, 8000,  "Cash"],
  [28, 3, 12000, "Bank"],
];

// ── component ──────────────────────────────────────────────────────────────────
const SECTIONS = [
  "Account Heads", "Suppliers", "Doctors", "Procedures",
  "Products", "Patients", "Opening Stock", "Purchases",
  "Appointments", "Invoices", "Expenses", "Income",
  "Journal Entries", "Supplier Advances",
];

const INIT_STATUS = Object.fromEntries(SECTIONS.map((s) => [s, "idle"]));

const statusBadge = (st) => {
  const map = { idle: ["secondary", "Pending"], running: ["warning", "Running…"], done: ["success", "Done"], error: ["danger", "Error"] };
  const [color, label] = map[st] || ["secondary", st];
  return <span className={`badge bg-${color}`}>{label}</span>;
};

// Fetch a list and return array safely
const fetchList = async (fn) => {
  try { const r = await fn(); return Array.isArray(r.data) ? r.data : []; }
  catch { return []; }
};

// Helper to get primary ID from any entity
const getId = (obj) => obj?.supplierID ?? obj?.doctorID ?? obj?.procedureID ?? obj?.productID ?? obj?.patientID ?? obj?.accountID ?? obj?.id;

export default function TestDataSeeder() {
  const [status,  setStatus]  = useState(INIT_STATUS);
  const [log,     setLog]     = useState([]);
  const [running, setRunning] = useState(false);
  const logRef = useRef(null);

  const mark   = (section, st) => setStatus((p) => ({ ...p, [section]: st }));
  const addLog = (msg, type = "info") => {
    const time = new Date().toLocaleTimeString();
    setLog((p) => [...p, { msg, type, time }]);
    setTimeout(() => logRef.current?.scrollTo(0, logRef.current.scrollHeight), 50);
  };

  const tryCreate = async (label, fn) => {
    try {
      const res = await fn();
      addLog(`✓ ${label}`, "success");
      return res?.data ?? null;
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.title || e?.response?.data || e.message || "Failed";
      addLog(`✗ ${label}: ${String(msg).slice(0, 120)}`, "error");
      return null;
    }
  };

  // Returns true if section was skipped (already has data)
  const skipIfExists = async (section, getFn) => {
    const list = await fetchList(getFn);
    if (list.length > 0) {
      addLog(`⏭ ${section}: already has ${list.length} record(s) — skipped`, "info");
      mark(section, "done");
      return true;
    }
    return false;
  };

  const seed = async () => {
    setRunning(true);
    setLog([]);
    setStatus(INIT_STATUS);

    // ── 1. Account Heads — skip by name ────────────────────────────────────────
    mark("Account Heads", "running");
    addLog("── Account Heads ──");
    const preAH = await fetchList(getAccountHeads);
    for (const ah of ACCOUNT_HEADS) {
      if (preAH.some((x) => x.accountName === ah.accountName)) {
        addLog(`⏭ ${ah.accountName}: already exists`); continue;
      }
      await tryCreate(ah.accountName, () => createAccountHead({ accountName: ah.accountName, status: "Active" }));
    }
    const ahList = await fetchList(getAccountHeads);
    const findAH = (name) => ahList.find((a) => a.accountName === name);
    const ahById     = ACCOUNT_HEADS.map((def) => findAH(def.accountName));
    const expenseAHs = ACCOUNT_HEADS.map((d, i) => d._type === "expense" ? ahById[i] : null).filter(Boolean);
    const incomeAHs  = ACCOUNT_HEADS.map((d, i) => d._type === "income"  ? ahById[i] : null).filter(Boolean);
    mark("Account Heads", ahById.filter(Boolean).length ? "done" : "error");

    // ── 2. Suppliers — skip by name ─────────────────────────────────────────────
    mark("Suppliers", "running");
    addLog("── Suppliers ──");
    const preSup = await fetchList(getSuppliers);
    for (const sup of SUPPLIERS) {
      if (preSup.some((x) => x.supplierName === sup.supplierName)) {
        addLog(`⏭ ${sup.supplierName}: already exists`); continue;
      }
      await tryCreate(sup.supplierName, () => createSupplier(sup));
    }
    const supList = await fetchList(getSuppliers);
    const supById = SUPPLIERS.map((def) => supList.find((s) => s.supplierName === def.supplierName));
    mark("Suppliers", supById.filter(Boolean).length ? "done" : "error");

    // ── 3. Doctors — skip by name ────────────────────────────────────────────────
    mark("Doctors", "running");
    addLog("── Doctors ──");
    const preDoc = await fetchList(getDoctors);
    for (const doc of DOCTORS) {
      if (preDoc.some((x) => x.doctorName === doc.doctorName)) {
        addLog(`⏭ ${doc.doctorName}: already exists`); continue;
      }
      await tryCreate(doc.doctorName, () => createDoctor(doc));
    }
    const docList = await fetchList(getDoctors);
    const docById = DOCTORS.map((def) => docList.find((d) => d.doctorName === def.doctorName));
    mark("Doctors", docById.filter(Boolean).length ? "done" : "error");

    // ── 4. Procedures — skip by name ─────────────────────────────────────────────
    mark("Procedures", "running");
    addLog("── Procedures ──");
    const preProc = await fetchList(getProcedures);
    for (const proc of PROCEDURES) {
      if (preProc.some((x) => x.procedureName === proc.procedureName)) {
        addLog(`⏭ ${proc.procedureName}: already exists`); continue;
      }
      await tryCreate(proc.procedureName, () => createProcedure(proc));
    }
    const procList = await fetchList(getProcedures);
    const procById = PROCEDURES.map((def) => procList.find((p) => p.procedureName === def.procedureName));
    mark("Procedures", procById.filter(Boolean).length ? "done" : "error");

    // ── 5. Products — skip by name ───────────────────────────────────────────────
    mark("Products", "running");
    addLog("── Products ──");
    const preProd = await fetchList(getProducts);
    for (const prod of PRODUCTS) {
      if (preProd.some((x) => x.itemName === prod.itemName)) {
        addLog(`⏭ ${prod.itemName}: already exists`); continue;
      }
      const defaultSupplierID = supById[0] ? getId(supById[0]) : null;
      await tryCreate(prod.itemName, () =>
        createProduct({ ...prod, defaultSupplierID, status: "Active" })
      );
    }
    const prodList = await fetchList(getProducts);
    const prodById = PRODUCTS.map((def) => prodList.find((p) => p.itemName === def.itemName));
    mark("Products", prodById.filter(Boolean).length ? "done" : "error");

    // ── 6. Patients — skip by name ───────────────────────────────────────────────
    mark("Patients", "running");
    addLog("── Patients ──");
    const prePat = await fetchList(getPatients);
    for (const pat of PATIENTS) {
      if (prePat.some((x) => x.patientName === pat.patientName)) {
        addLog(`⏭ ${pat.patientName}: already exists`); continue;
      }
      await tryCreate(pat.patientName, () => createPatient(pat));
    }
    const patientList = await fetchList(getPatients);
    const patById = PATIENTS.map((def) => patientList.find((p) => p.patientName === def.patientName));
    mark("Patients", patById.filter(Boolean).length ? "done" : "error");

    // ── 7. Opening Stock — skip if any records exist ──────────────────────────────
    if (!(await skipIfExists("Opening Stock", getOpeningStocks))) {
      mark("Opening Stock", "running");
      addLog("── Opening Stock ──");
      const openingItems = [
        [0, 25, 200], [1, 100, 50], [2, 10, 500], [3, 6, 1200], [4, 30, 80],
      ];
      let osOk = 0;
      for (const [pi, qty, rate] of openingItems) {
        const prod = prodById[pi];
        if (!prod) { addLog(`✗ Opening stock item ${pi}: product not found`, "error"); continue; }
        const r = await tryCreate(`Stock: ${PRODUCTS[pi].itemName} ×${qty}`, () =>
          createOpeningStock({ asOnDate: ago(31), productID: getId(prod), quantity: qty, purchaseRate: rate })
        );
        if (r !== null) osOk++;
      }
      mark("Opening Stock", osOk ? "done" : "error");
    }

    // ── 8. Purchases — skip if any records exist ──────────────────────────────────
    if (!(await skipIfExists("Purchases", getPurchases))) {
      mark("Purchases", "running");
      addLog("── Purchases ──");
      let purOk = 0;
      for (const [d, si, items] of PURCHASE_PLAN) {
        const sup = supById[si];
        if (!sup) { addLog(`✗ Purchase: supplier index ${si} not available`, "error"); continue; }
        const details = items
          .map(({ pi, qty, rate }) => {
            const pr = prodById[pi];
            return pr ? { productID: getId(pr), quantity: qty, purchaseRate: rate, discount: 0 } : null;
          })
          .filter(Boolean);
        if (!details.length) continue;
        const total = details.reduce((s, i) => s + i.quantity * i.purchaseRate, 0);
        const r = await tryCreate(`Purchase from ${SUPPLIERS[si].supplierName} on ${ago(d)}`, () =>
          createPurchase({
            supplierID: getId(sup),
            invoiceNumber: `SINV-${ago(d)}-${si}`,
            purchaseDate: ago(d),
            paymentMode: "Credit",
            totalAmount: total,
            items: details,
          })
        );
        if (r !== null) purOk++;
      }
      mark("Purchases", purOk ? "done" : "error");
    }

    // ── 9. Appointments — skip if any records exist ───────────────────────────────
    const apptById = new Array(APPT_PLAN.length).fill(null);
    if (!(await skipIfExists("Appointments", () => getAppointments({})))) {
      mark("Appointments", "running");
      addLog("── Appointments ──");
      for (let i = 0; i < APPT_PLAN.length; i++) {
        const [d, time, pi, di, proci, apptStatus] = APPT_PLAN[i];
        const pat  = patById[pi];
        const doc  = docById[di];
        const proc = procById[proci];
        if (!pat || !doc || !proc) {
          addLog(`✗ Appointment ${ago(d)} ${time}: missing patient/doctor/procedure`, "error");
          continue;
        }
        const r = await tryCreate(
          `Appt: ${PATIENTS[pi].patientName} – ${PROCEDURES[proci].procedureName} on ${ago(d)}`,
          () => createAppointment({
            appointmentDate: ago(d),
            fromTime: time,
            patientId:   getId(pat),
            doctorId:    getId(doc),
            procedureId: getId(proc),
            status: apptStatus,
            remarks: "",
          })
        );
        apptById[i] = r ?? null;
      }
      mark("Appointments", apptById.filter(Boolean).length ? "done" : "error");
    }

    // ── 10. Invoices — skip if any records exist ──────────────────────────────────
    if (!(await skipIfExists("Invoices", getInvoices))) {
      mark("Invoices", "running");
      addLog("── Invoices ──");
      let invOk = 0;
      for (const [ai, disc, paidFull] of INVOICE_PLAN) {
        const [d, , pi, di, proci] = APPT_PLAN[ai];
        const pat  = patById[pi];
        const doc  = docById[di];
        const proc = procById[proci];
        if (!pat || !doc || !proc) continue;
        const rate    = PROCEDURES[proci].defaultRate;
        const net     = rate - disc;
        const paid    = paidFull ? net : Math.floor(net * 0.5);
        const balance = net - paid;
        const r = await tryCreate(
          `Invoice: ${PATIENTS[pi].patientName} ₹${net} on ${ago(d)}`,
          () => createInvoice({
            invoiceDate:   ago(d),
            patientId:     getId(pat),
            doctorId:      getId(doc),
            appointmentId: apptById[ai] ? getId(apptById[ai]) : null,
            subtotal: rate,
            overallDiscount: disc,
            taxAmount: 0,
            netTotal: net,
            paidAmount: paid,
            balanceAmount: balance,
            invoiceStatus: balance > 0 ? "PartialPaid" : "Paid",
            services: [{ procedureId: getId(proc), qty: 1, rate, discount: disc, amount: net }],
            payments: [{ paymentDate: ago(d), paymentMode: "Cash", amount: paid }],
          })
        );
        if (r !== null) invOk++;
      }
      mark("Invoices", invOk ? "done" : "error");
    }

    // ── 11. Expenses — skip if any records exist ──────────────────────────────────
    if (!(await skipIfExists("Expenses", () => getExpenses({})))) {
      mark("Expenses", "running");
      addLog("── Expenses ──");
      let expOk = 0;
      for (const [d, ahIdx, amount, mode, desc] of EXPENSE_PLAN) {
        const ah = expenseAHs[ahIdx % Math.max(expenseAHs.length, 1)];
        if (!ah) { addLog(`✗ Expense: no expense account heads available`, "error"); break; }
        const r = await tryCreate(`Expense: ${desc} ₹${amount} on ${ago(d)}`, () =>
          createExpense({ expenseDate: ago(d), accountHeadId: getId(ah), amount, paymentMode: mode, description: desc })
        );
        if (r !== null) expOk++;
      }
      mark("Expenses", expOk ? "done" : "error");
    }

    // ── 12. Income — skip if any records exist ────────────────────────────────────
    if (!(await skipIfExists("Income", () => getIncomeEntries({})))) {
      mark("Income", "running");
      addLog("── Income Entries ──");
      let incOk = 0;
      for (const [d, ahIdx, amount, mode, desc] of INCOME_PLAN) {
        const incIdx = ahIdx - 4;
        const ah = incomeAHs[Math.max(incIdx, 0) % Math.max(incomeAHs.length, 1)];
        if (!ah) { addLog(`✗ Income: no income account heads available`, "error"); break; }
        const r = await tryCreate(`Income: ${desc} ₹${amount} on ${ago(d)}`, () =>
          createIncomeEntry({ incomeDate: ago(d), accountHeadId: getId(ah), amount, paymentMode: mode, description: desc })
        );
        if (r !== null) incOk++;
      }
      mark("Income", incOk ? "done" : "error");
    }

    // ── 13. Journal Entries — skip if any records exist ───────────────────────────
    if (!(await skipIfExists("Journal Entries", () => getJournalEntries({})))) {
      mark("Journal Entries", "running");
      addLog("── Journal Entries ──");
      let jrOk = 0;
      if (expenseAHs.length >= 1 && incomeAHs.length >= 1) {
        const cashAH = findAH("Cash");
        const bankAH = findAH("Bank");
        const jePlan = [
          [5,  cashAH || expenseAHs[0], expenseAHs[0],               5000, "Cash payment for dental supplies"],
          [15, bankAH || expenseAHs[1], incomeAHs[0],                8000, "Bank receipt insurance reimbursement"],
          [25, cashAH || expenseAHs[0], incomeAHs[1] || incomeAHs[0], 2000, "Cash income miscellaneous"],
        ];
        for (const [d, debitAH, creditAH, amount, narration] of jePlan) {
          if (!debitAH || !creditAH) continue;
          const r = await tryCreate(`Journal: ${narration} on ${ago(d)}`, () =>
            createJournalEntry({
              journalDate: ago(d),
              narration,
              lines: [
                { accountHeadId: getId(debitAH),  debitAmount: amount, creditAmount: 0 },
                { accountHeadId: getId(creditAH), debitAmount: 0,      creditAmount: amount },
              ],
            })
          );
          if (r !== null) jrOk++;
        }
      } else {
        addLog("✗ Journal Entries: not enough account heads", "error");
      }
      mark("Journal Entries", jrOk ? "done" : "error");
    }

    // ── 14. Supplier Advances — skip if any records exist ─────────────────────────
    if (!(await skipIfExists("Supplier Advances", () => getSupplierAdvances({})))) {
      mark("Supplier Advances", "running");
      addLog("── Supplier Advances ──");
      let advOk = 0;
      for (const [d, si, amount, mode] of ADVANCE_PLAN) {
        const sup = supById[si];
        if (!sup) { addLog(`✗ Advance: supplier index ${si} not available`, "error"); continue; }
        const r = await tryCreate(`Advance: ${SUPPLIERS[si].supplierName} ₹${amount} on ${ago(d)}`, () =>
          createSupplierAdvance({
            supplierID:  getId(sup),
            advanceDate: ago(d),
            amount,
            paymentMode: mode,
            remarks: "Advance payment",
          })
        );
        if (r !== null) advOk++;
      }
      mark("Supplier Advances", advOk ? "done" : "error");
    }

    addLog("═══ Seeding complete! Navigate to any module to verify data. ═══", "success");
    setRunning(false);
  };

  const allDone   = SECTIONS.every((s) => status[s] === "done");
  const anyError  = SECTIONS.some((s)  => status[s] === "error");
  const doneCount = SECTIONS.filter((s) => status[s] === "done").length;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Test Data Seeder</h4>
            <h6>Creates sample masters, transactions and report data</h6>
          </div>
        </div>

        <div className="row">
          {/* Left — sections */}
          <div className="col-lg-5">
            <div className="card">
              <div className="card-header"><h5 className="card-title mb-0">Seeding Sections</h5></div>
              <div className="card-body p-0">
                <table className="table mb-0">
                  <thead><tr><th>#</th><th>Section</th><th>Records</th><th>Status</th></tr></thead>
                  <tbody>
                    {[
                      ["Account Heads",    ACCOUNT_HEADS.length],
                      ["Suppliers",        SUPPLIERS.length],
                      ["Doctors",          DOCTORS.length],
                      ["Procedures",       PROCEDURES.length],
                      ["Products",         PRODUCTS.length],
                      ["Patients",         PATIENTS.length],
                      ["Opening Stock",    5],
                      ["Purchases",        PURCHASE_PLAN.length],
                      ["Appointments",     APPT_PLAN.length],
                      ["Invoices",         INVOICE_PLAN.length],
                      ["Expenses",         EXPENSE_PLAN.length],
                      ["Income",           INCOME_PLAN.length],
                      ["Journal Entries",  3],
                      ["Supplier Advances",ADVANCE_PLAN.length],
                    ].map(([name, count], i) => (
                      <tr key={name}>
                        <td className="text-muted small">{i + 1}</td>
                        <td><strong>{name}</strong></td>
                        <td className="text-muted">{count}</td>
                        <td>{statusBadge(status[name])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card-footer d-flex justify-content-between align-items-center">
                <span className="text-muted small">{doneCount} / {SECTIONS.length} sections done</span>
                <button
                  className={`btn ${anyError ? "btn-warning" : "btn-success"}`}
                  onClick={seed}
                  disabled={running}
                >
                  {running
                    ? <><span className="spinner-border spinner-border-sm me-2" />Seeding…</>
                    : allDone ? "Re-Seed Data" : anyError ? "Retry Seed" : "Seed All Test Data"}
                </button>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-header"><h5 className="card-title mb-0">What gets created</h5></div>
              <div className="card-body">
                <ul className="mb-0 small text-muted" style={{ lineHeight: 2 }}>
                  <li>Appointments spread across <strong>today, this week, this month, and 30 days</strong></li>
                  <li>Invoices with <strong>Paid</strong> and <strong>Partial Paid</strong> statuses</li>
                  <li>Expenses and income to populate the <strong>finance reports</strong></li>
                  <li>Purchases to show <strong>stock movement and payables</strong></li>
                  <li>Opening stock so the <strong>current stock page</strong> has data</li>
                  <li>Supplier advances for <strong>payables report</strong></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Right — live log */}
          <div className="col-lg-7">
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Activity Log</h5>
                {log.length > 0 && (
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setLog([])}>Clear</button>
                )}
              </div>
              <div
                ref={logRef}
                className="card-body p-2"
                style={{ height: 560, overflowY: "auto", fontFamily: "monospace", fontSize: 12, background: "#1a1a2e", color: "#e0e0e0", borderRadius: "0 0 8px 8px" }}
              >
                {log.length === 0 ? (
                  <div className="text-muted p-3">Click &quot;Seed All Test Data&quot; to begin…</div>
                ) : (
                  log.map((entry, i) => (
                    <div
                      key={i}
                      style={{
                        color: entry.type === "success" ? "#4ade80"
                             : entry.type === "error"   ? "#f87171"
                             : entry.msg.startsWith("──") ? "#facc15"
                             : entry.msg.startsWith("═══") ? "#38bdf8"
                             : "#e0e0e0",
                        padding: "1px 8px",
                      }}
                    >
                      <span style={{ opacity: 0.5 }}>[{entry.time}]</span> {entry.msg}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
